import { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { Button } from '@ludolio/shared-ui';
import { Input } from '@ludolio/shared-ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ludolio/shared-ui';
import { Badge } from '@ludolio/shared-ui';
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Mail,
  Calendar,
  Trash2,
  Loader2,
  Send,
} from 'lucide-react';
import type { UnitStudent } from './types';
import { format } from 'date-fns';

interface UnitStudentsTableProps {
  students: UnitStudent[];
  onRemoveStudent?: (student: UnitStudent) => void;
  onResendInvitation?: (student: UnitStudent) => void;
  resendingInvitationId?: string | null;
}

export function UnitStudentsTable({
  students,
  onRemoveStudent,
  onResendInvitation,
  resendingInvitationId,
}: UnitStudentsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const columns: ColumnDef<UnitStudent>[] = useMemo(
    () => [
      {
        accessorKey: 'firstName',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="-ml-4"
            >
              First Name
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="ml-2 h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="ml-2 h-4 w-4" />
              ) : (
                <ArrowUpDown className="ml-2 h-4 w-4" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const firstName = row.original.firstName || '-';
          return <div className="font-medium">{firstName}</div>;
        },
      },
      {
        accessorKey: 'lastName',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="-ml-4"
            >
              Last Name
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="ml-2 h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="ml-2 h-4 w-4" />
              ) : (
                <ArrowUpDown className="ml-2 h-4 w-4" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => (
          <div className="font-medium">{row.original.lastName || '-'}</div>
        ),
      },
      {
        accessorKey: 'email',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="-ml-4"
            >
              <Mail className="mr-2 h-4 w-4" />
              Email
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="ml-2 h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="ml-2 h-4 w-4" />
              ) : (
                <ArrowUpDown className="ml-2 h-4 w-4" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => <div className="text-sm">{row.getValue('email')}</div>,
      },
      {
        accessorKey: 'totalPlayingHours',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="-ml-4"
            >
              <Clock className="mr-2 h-4 w-4" />
              Playing Time
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="ml-2 h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="ml-2 h-4 w-4" />
              ) : (
                <ArrowUpDown className="ml-2 h-4 w-4" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const student = row.original;
          if (student.status === 'pending') {
            return <span className="text-muted-foreground">-</span>;
          }
          const hours = row.getValue('totalPlayingHours') as number;
          return (
            <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/20">
              {hours}h
            </Badge>
          );
        },
      },
      {
        accessorKey: 'lastActive',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="-ml-4"
            >
              <Calendar className="mr-2 h-4 w-4" />
              Last Active
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="ml-2 h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="ml-2 h-4 w-4" />
              ) : (
                <ArrowUpDown className="ml-2 h-4 w-4" />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const student = row.original;
          if (student.status === 'pending') {
            const labels: Record<string, string> = {
              not_sent: 'Email not sent',
              queued: 'Queued',
              retry: 'Retrying',
              sending: 'Sending',
              sent: 'Pending',
              expired: 'Expired',
              failed: 'Send failed',
              uncertain: 'Delivery uncertain',
            };
            const status = student.invitationStatus || 'not_sent';
            const isHealthy = ['queued', 'retry', 'sending', 'sent'].includes(status);
            return <Badge variant={isHealthy ? 'secondary' : 'destructive'}>{labels[status] || 'Pending'}</Badge>;
          }
          if (!student.lastActive) {
            return <span className="text-muted-foreground">Never</span>;
          }
          return (
            <div className="text-sm">
              {format(new Date(student.lastActive), 'MMM dd, yyyy')}
            </div>
          );
        },
      },
      ...(onRemoveStudent ? [{
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="flex justify-end gap-1">
            {student.status === 'pending' && onResendInvitation && (
              <Button
                variant="ghost"
                size="icon"
                disabled={Boolean(resendingInvitationId)}
                onClick={() => onResendInvitation(student)}
                aria-label={`Resend invitation to ${student.email}`}
                title="Resend invitation"
              >
                {resendingInvitationId === student.id
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Send className="h-4 w-4" />}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemoveStudent(student)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              aria-label={`Remove ${student.email}`}
              title={`Remove ${student.email}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            </div>
          );
        },
      } satisfies ColumnDef<UnitStudent>] : []),
    ],
    [onRemoveStudent, onResendInvitation, resendingInvitationId]
  );

  const table = useReactTable({
    data: students,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    state: {
      sorting,
      globalFilter,
    },
    initialState: {
      pagination: { pageSize: 10 },
    },
  });

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search students..."
          aria-label="Search students"
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    aria-sort={
                      header.column.getIsSorted() === 'asc'
                        ? 'ascending'
                        : header.column.getIsSorted() === 'desc'
                          ? 'descending'
                          : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No students found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} student(s)
        </div>
        <div className="flex max-w-full items-center gap-1 overflow-x-auto pb-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            aria-label="First page"
            title="First page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
            title="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            aria-label="Last page"
            title="Last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

